import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import FooterTop from "../../components/footer-top";
import oneImg from "../../assets/img/steps/one.png";
import twoImg from "../../assets/img/steps/two.png";
import threeImg from "../../assets/img/steps/three.png";
import service1 from "../../assets/img/background/service1.png";
import service2 from "../../assets/img/background/service2.png";
import service3 from "../../assets/img/background/service3.png";
import service4 from "../../assets/img/background/service4.png";
import { servicesData } from "../../data/servicesData";
import {
  CheckCircle,
  SearchIcon,
  ClockIcon,
  CheckCircleIcon,
} from "lucide-react";
import { useSearchLocation } from "../../store/searchLocation";
import { useServiceStore } from "../../store/serviceStore";
import { MapPin, Locate, ChevronDown } from "lucide-react";
import { SEO } from "../../components/SEO";
import { useMetaDataStore } from "../../store/metadataStore";
import { createHmacSignature } from "../../utils/hmac-signature";
import { API } from "../../utils/axios";
import { useMetaStore } from "../../store/data";

const staticStates = [
  {
    zone_id: 610,
    name: "Ontario",
    cities: [
      { city_id: 1, city_name: "Toronto" },
      { city_id: 2, city_name: "Mississauga" },
      { city_id: 3, city_name: "Brampton" },
      { city_id: 4, city_name: "Ottawa" },
      { city_id: 5, city_name: "Hamilton" },
      { city_id: 6, city_name: "London" },
      { city_id: 7, city_name: "Kitchener" },
      { city_id: 8, city_name: "Waterloo" }
    ]
  },
  {
    zone_id: 603,
    name: "British Columbia",
    cities: [
      { city_id: 9, city_name: "Vancouver" },
      { city_id: 10, city_name: "Surrey" },
      { city_id: 11, city_name: "Burnaby" },
      { city_id: 12, city_name: "Richmond" },
      { city_id: 13, city_name: "Victoria" },
      { city_id: 14, city_name: "Kelowna" }
    ]
  },
  {
    zone_id: 602,
    name: "Alberta",
    cities: [
      { city_id: 15, city_name: "Calgary" },
      { city_id: 16, city_name: "Edmonton" },
      { city_id: 17, city_name: "Red Deer" },
      { city_id: 18, city_name: "Lethbridge" },
      { city_id: 19, city_name: "Airdrie" },
      { city_id: 20, city_name: "Chestermere" }
    ]
  }
];

const staticServices = [
  { servicetype_id: 1, name: "Physiotherapy" },
  { servicetype_id: 2, name: "Chiropractic" },
  { servicetype_id: 3, name: "Acupuncture" },
  { servicetype_id: 4, name: "Massage Therapy" }
];

export default function Home() {
  const navigate = useNavigate();
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const DROPDOWN_HEIGHT = 260; // match max-h-64 (~256px)
  const [location, setLocation] = useState("");
  const [locationId, setLocationId] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [openServiceDropdown, setOpenServiceDropdown] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const serviceDropdownRef = useRef<HTMLDivElement>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const { services, setServices } = useServiceStore();
  const { updateSearchLocation } = useSearchLocation();
  const [activeStep, setActiveStep] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [locationError, setLocationError] = useState<string | null>();
  const [serviceError, setServiceError] = useState<string | null>();
  const { states } = useMetaDataStore();
  const { serviceTypes } = useMetaStore();

  const steps = [
    {
      id: 1,
      title: "Find Your Practitioner",
      description:
        "Search by location and service type to find qualified professionals near you.",
      subInfo:
        "You can filter by specialty, availability, and user reviews to find the perfect match for your needs.",
      icon: SearchIcon,
      image: oneImg,
    },
    {
      id: 2,
      title: "Book Your Appointment",
      description:
        "Select your preferred time slot and confirm your appointment details.",
      subInfo:
        "Our real-time calendar shows you available slots, making scheduling quick and hassle-free.",
      icon: ClockIcon,
      image: twoImg,
    },
    {
      id: 3,
      title: "Get Confirmed",
      description:
        "Receive instant or quick confirmation from your chosen practitioner.",
      subInfo:
        "You'll receive a confirmation email and a reminder before your appointment.",
      icon: CheckCircleIcon,
      image: threeImg,
    },
  ];

  const bgImages = [service1, service2, service3, service4];

  const calculateDropdownPosition = (ref: any) => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
      setDropdownPosition("top");
    } else {
      setDropdownPosition("bottom");
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bgImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [bgImages.length]);

  useEffect(() => {
    async function getDefultBusinesses() {
      try {
        const key = await createHmacSignature("defaulters");
        const businessData = await API.post("/business/data", {
          data: key,
        });
        if (businessData.data.businesses.length === 0) {
          return;
        }
        setServices(businessData.data.businesses);
      } catch (err) {
        console.log("error in getDefultBusinesses", err);
      }
    }
    if (services.length === 0) {
      getDefultBusinesses();
    }
  }, [services]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        serviceDropdownRef.current &&
        !serviceDropdownRef.current.contains(event.target as Node)
      ) {
        setOpenServiceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const closeAllDropdowns = () => {
      setIsOpen(false); // location dropdown
      setOpenServiceDropdown(false); // service dropdown
    };

    window.addEventListener("scroll", closeAllDropdowns);
    window.addEventListener("resize", closeAllDropdowns);

    return () => {
      window.removeEventListener("scroll", closeAllDropdowns);
      window.removeEventListener("resize", closeAllDropdowns);
    };
  }, []);

  return (
    <main>
      <SEO
        title="Wellness Booking Platform in Canada | Instant Appointment Booking for Physiotherapy & Massage"
        description="Last Minute Wellness makes it simple to book physiotherapy, chiropractic, and massage appointments instantly across Canada — all in one place."
        url="https://lastminutewellness.ca/"
        image="https://lastminutewellness.ca/banner.png"
      />
      {/* Search Section */}
      <div className="relative w-full h-[600px] lg:h-[620px]">
        {bgImages.map((img, index) => (
          <div
            key={index}
            className={`absolute bg-cover bg-center   inset-0  transition-opacity duration-1000 ${
              index === currentIndex ? "opacity-100" : "opacity-0"
            }`}
            style={{ backgroundImage: `url(${img})` }}
          ></div>
        ))}
        <div className="absolute inset-0 bg-black/40"></div>
        {/* Deep Teal Overlay */}
        <div className="relative max-w-8xl mx-auto px-12 sm:px-6 md:px-12 h-full flex flex-col justify-center items-start">
          <h2 className="text-4xl sm:text-5xl md:text-[60px] font-semibold text-white mb-8 sm:mb-6 leading-tight max-w-xl sm:max-w-2xl md:max-w-3xl drop-shadow-lg">
            Last-Minute Appointments First-class care
          </h2>
          <p className="text-lg sm:text-xl md:text-[28px] font-medium text-white mb-3 sm:mb-14">
            Feel better, Even on your busiest days
          </p>
          {/* <h2 className="text-4xl sm:text-5xl md:text-[60px] font-medium text-white mb-8 sm:mb-10 leading-tight max-w-xl sm:max-w-2xl md:max-w-3xl drop-shadow-lg">
            Last-Minute Appointments,{" "}
            <span className="text-white-300">First-class care</span>
          </h2> */}

          {/* Search Box */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white rounded-2xl p-4 w-full max-w-xl sm:max-w-4xl border border-gray-200 shadow-md">
            <div className="w-full max-w-md mx-auto relative" ref={dropdownRef}>
              <div
                className={`relative flex items-center w-full rounded-2xl px-4 py-3 transition-all shadow-sm cursor-pointer
            ${
              locationError
                ? "bg-red-50 ring-2 ring-red-400 animate-shake"
                : "bg-blue-50 hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500"
            }`}
                onClick={() => {
                  setIsOpen(!isOpen);
                  if (locationError) setLocationError(null);
                }}
              >
                <MapPin
                  className={`${locationError ? "text-red-500" : "text-blue-500"} w-5 h-5 mr-3 flex-shrink-0`}
                />
                <span
                  className={`flex-1 text-sm sm:text-base lg:text-lg truncate ${
                    locationError
                      ? "text-red-400"
                      : !location
                        ? "text-slate-600"
                        : "text-gray-700"
                  }`}
                >
                  {location
                    ? location
                    : locationError
                      ? locationError
                      : "Select City"}
                </span>
                <ChevronDown
                  className={`text-gray-500 w-4 h-4 ml-2 transform transition-transform ${isOpen ? "rotate-180" : ""}`}
                />

                {/* Dropdown menu */}
                {isOpen && (
                  <div
                    className={`absolute left-0 ${dropdownPosition === "bottom" ? "top-[115%] slide-in-from-top-2" : "bottom-[110%] slide-in-from-bottom-2"}
 w-full bg-white shadow-xl rounded-2xl border border-blue-100 z-50 max-h-64 overflow-y-auto animate-in fade-in zoom-in-95 duration-150`}
                  >
                    {staticStates.map((state) => (
                      <div key={state.zone_id}>
                        <div className="bg-blue-50 text-blue-600 font-semibold text-sm sm:text-base px-4 py-2">
                          {state.name}
                        </div>
                        {state.cities.map((city) => (
                          <div
                            key={city.city_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocation(city.city_name);
                              setLocationError(null);
                              setLocationId(city.city_id);
                              setIsOpen(false);
                              updateSearchLocation({
                                location: city.city_name,
                                locationId: city.city_id,
                                liveLocation: false,
                              });
                            }}
                            className={`px-4 py-2 text-gray-700 hover:bg-blue-100 cursor-pointer text-sm sm:text-base transition ${
                              locationId === city.city_id
                                ? "bg-blue-100 font-semibold text-blue-700"
                                : ""
                            }`}
                          >
                            {city.city_name}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div
              className="w-full max-w-md mx-auto relative"
              ref={serviceDropdownRef}
            >
              <div
                className={`relative flex items-center w-full rounded-2xl px-4 py-3 transition-all shadow-sm cursor-pointer
            ${
              serviceError
                ? "bg-red-50 ring-2 ring-red-400 animate-shake"
                : "bg-blue-50 hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500"
            }`}
                onClick={() => {
                  {
                  calculateDropdownPosition(serviceDropdownRef);
                  setOpenServiceDropdown(!openServiceDropdown);
                  if (serviceError) setServiceError(null);
                };
                }}
              >
                <span
                  className={`flex-1 text-sm sm:text-base lg:text-lg truncate ${
                    serviceError
                      ? "text-red-400"
                      : !selectedService
                        ? "text-slate-600"
                        : "text-gray-700"
                  }`}
                >
                  {selectedService
                    ? selectedService
                    : serviceError
                      ? serviceError
                      : "Select Service"}
                </span>
                <ChevronDown
                  className={`text-gray-500 w-4 h-4 ml-2 transform transition-transform ${openServiceDropdown ? "rotate-180" : ""}`}
                />

                {/* Dropdown menu */}
                {openServiceDropdown && (
                  <div
                    className={`absolute left-0 ${dropdownPosition === "bottom" ? "top-[115%] slide-in-from-top-2" : "bottom-[110%] slide-in-from-bottom-2"}
 w-full bg-white shadow-xl rounded-2xl border border-blue-100 z-50 max-h-64 overflow-y-auto animate-in fade-in zoom-in-95 duration-150`}
                  >
                    {staticServices.map((service) => (
                      <div
                        key={service.servicetype_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenServiceDropdown(false);
                          setSelectedService(service.name);
                          setServiceError(null);;
                          updateSearchLocation({
                            userSelectedService: service.servicetype_id,
                          });
                        }}
                        className={`px-4 py-2 text-gray-700 hover:bg-blue-100 cursor-pointer text-sm sm:text-base transition ${
                          selectedService === service.name
                            ? "bg-blue-100 font-semibold text-blue-700"
                            : ""
                        }`}
                      >
                        {service.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-row gap-2 w-full">
              {/* Locate Button */}
              <button
                /* Added 'group' and 'relative' here */
                className="group relative flex items-center justify-center gap-2 h-12 px-3 sm:px-4 text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition flex-1 min-w-0"
                onClick={() => {
                  updateSearchLocation({ locationId: 0, liveLocation: true });
                  navigate("/serviceList");
                }}
                disabled={isLoadingLocation}
              >
                <Locate className="w-5 h-5" />

                {/* Tooltip Wrapper */}
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 scale-50 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 pointer-events-none z-50">
                  <div className="relative bg-gray-900 text-white text-[10px] font-medium py-2 px-3 rounded-lg whitespace-nowrap">
                    Use Current Location
                    {/* Tooltip Arrow */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                  </div>
                </div>
              </button>

              {/* Search Button */}
              <button
                className="h-12 px-3 sm:px-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold transition flex-[2] min-w-0 hover:from-blue-700 hover:to-indigo-800 hover:shadow-lg active:scale-95"
                onClick={() => {
                  if (!location) {
                    setLocationError("Please select city first");
                    return;
                  }
                  if (!selectedService) {
                    setServiceError("Please select service first");
                    return;
                  }
                  navigate(`/serviceList`);
                }}
              >
                Search
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-10 right-20 transform translate-x-1/2 flex items-center gap-2">
          {/* Left Arrow */}
          <button
            onClick={() =>
              setCurrentIndex((prev) =>
                prev === 0 ? bgImages.length - 1 : prev - 1,
              )
            }
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md border border-white/30 shadow-lg flex items-center justify-center
               hover:bg-white/30 hover:scale-110 transition-all duration-300"
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          {/* Right Arrow */}
          <button
            onClick={() =>
              setCurrentIndex((prev) =>
                prev === bgImages.length - 1 ? 0 : prev + 1,
              )
            }
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md border border-white/30 shadow-lg flex items-center justify-center
               hover:bg-white/30 hover:scale-110 transition-all duration-300"
          >
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className="p-10 sm:p-10   mx-auto relative z-10">
        <div className="text-center mb-6 md:mb-16 pt-9 md:pt-24">
          <h2 className="text-2xl lg:text-5xl font-semibold text-slate-900 leading-[1.15] mb-3">
            Our <span className="text-white-600">Premium</span> Wellness
            Services
          </h2>
          <p className="text-lg md:text-2xl text-slate-600 max-w-5xl mx-auto font-medium">
            Discover a range of professional health and wellness services
            tailored to your needs.
          </p>
        </div>

        <div className="flex flex-col mb-16 md:flex-row items-center justify-center lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-1 lg:!grid-cols-4 gap-4 w-full">
            {servicesData.map((service, index) => (
              <div
                onClick={() => navigate(`/service/${service.url}`)}
                key={index}
                className="group bg-white rounded-xl shadow-sm border border-gray-100 transition duration-500 ease-in-out hover:shadow-2xl hover:border-emerald-300 text-center cursor-pointer overflow-hidden transform hover:-translate-y-1"
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="rounded-t-xl mb-0 object-cover w-full h-48 transition duration-500 ease-in-out group-hover:scale-100"
                />
                <p className="font-medium text-base md:text-xl text-gray-700 p-4 group-hover:text-gray-800 transition">
                  {service.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Steps for users */}
      <div className="min-h-screen font-sans text-gray-800 antialiased py-10 lg:py-14">
        <div className="lg:mx-20 px-4 sm:px-6 md:px-12 lg:px-20 xl:px-24">
          <div className="text-center mb-20">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-semibold text-slate-900 leading-[1.15]">
              Our <span>Simple</span> Process
            </h1>
            <p className="mt-2 text-base sm:text-lg md:text-xl lg:text-2xl text-gray-800 max-w-2xl md:max-w-3xl mx-auto">
              Book your wellness appointment in three simple, stress-free steps.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row lg:space-x-16 items-center lg:items-start">
            {/* Step-by-Step Content */}
            <div className="flex-1 w-full space-y-6">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`flex items-start transition-all duration-500 ease-in-out cursor-pointer p-4 rounded-2xl sm:rounded-xl border ${
                    activeStep === step.id
                      ? " border-emerald-300 shadow-sm"
                      : "bg-white border-gray-200"
                  } hover: hover:border-emerald-200`}
                  onMouseEnter={() => setActiveStep(step.id)}
                >
                  <div
                    className={`flex-shrink-0 flex items-center justify-center p-3 rounded-xl transition-colors duration-500 ${
                      activeStep === step.id
                        ? "bg-emerald-600 text-white shadow-sm shadow-emerald-500/30"
                        : "bg-gray-100 text-emerald-500"
                    }`}
                  >
                    <step.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </div>
                  <div className="ml-4 sm:ml-6">
                    <h3
                      className={`text-lg sm:text-xl md:text-2xl font-medium transition-colors duration-500 ${
                        activeStep === step.id
                          ? "text-emerald-700"
                          : "text-gray-900"
                      }`}
                    >
                      {step.title}
                    </h3>
                    <p className="mt-1 text-base leading-snug text-gray-700 font-normal">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Image Container */}
            <div className="flex-1 w-full mt-10 lg:mt-0 relative h-64 sm:h-72 md:h-80 lg:h-96 xl:h-[420px] rounded-2xl sm:rounded-3xl overflow-hidden border-2 sm:border-4 border-gray-100">
              {steps.map((step) => (
                <img
                  key={step.id}
                  src={step.image}
                  alt={step.title}
                  className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ease-in-out ${
                    activeStep === step.id ? "opacity-100" : "opacity-0"
                  }`}
                />
              ))}
              <div className="absolute inset-0 bg-black/10"></div>
              {/* Subtle gradient overlay */}
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section id="about" className="py-20 overflow-hidden bg-gradient-to-b from-white via-blue-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left: Twin Staggered Columns Collage */}
            <div className="relative w-full flex gap-4 items-start group perspective-1000">
              {/* Decorative Background - Animated */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[130%] h-[130%] bg-blue-100/40 rounded-full blur-3xl -z-10 animate-pulse-slow"></div>
              
              {/* Column 1 - Left */}
              <div className="flex flex-col gap-6 w-1/2 animate-in slide-in-from-bottom-10 fade-in duration-1000"> 
                 {/* Img 1 */}
                 <div className="relative rounded-3xl overflow-hidden shadow-2xl border-[6px] border-white ring-1 ring-black/5 cursor-pointer transition-all duration-700 hover:-translate-y-2 hover:shadow-blue-200/50 bg-white">
                    <img src={service1} alt="Massage" className="w-full h-40 object-contain hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/10 to-transparent"></div>
                 </div>
                 {/* Img 2 */}
                 <div className="relative mt-6 rounded-3xl overflow-hidden shadow-2xl border-[6px] border-white ring-1 ring-black/5 cursor-pointer transition-all duration-700 hover:-translate-y-2 hover:shadow-emerald-200/50 bg-white">
                    <img src={service3} alt="Acupuncture" className="w-full h-32 object-contain hover:scale-110 transition-transform duration-700" />
                 </div>
              </div>

              {/* Column 2 - Right - Staggered */}
               <div className="flex flex-col gap-6 w-1/2 pt-16 animate-in slide-in-from-bottom-16 fade-in duration-1000 delay-200"> 
                 {/* Img 3 */}
                 <div className="relative rounded-3xl overflow-hidden shadow-2xl border-[6px] border-white ring-1 ring-black/5 cursor-pointer transition-all duration-700 hover:-translate-y-2 hover:shadow-indigo-200/50 bg-white">
                    <img src={service2} alt="Chiro" className="w-full h-36 object-contain hover:scale-110 transition-transform duration-700" />
                 </div>
                 {/* Img 4 */}
                 <div className="relative rounded-3xl overflow-hidden shadow-2xl border-[6px] border-white ring-1 ring-black/5 cursor-pointer transition-all duration-700 hover:-translate-y-2 hover:shadow-teal-200/50 bg-white">
                    <img src={service4} alt="Physio" className="w-full h-40 object-contain hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/10 to-transparent"></div>
                 </div>
              </div>

               {/* Floating Badge - Animated Pop-In */}
               <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-white/50 z-30 flex  gap-4 animate-in zoom-in fade-in duration-700 delay-500 hover:scale-105 transition-transform cursor-default">
                  <div>
                    <div className="flex text-amber-400 text-sm gap-0.5 mb-0.5">
                       {[1,2,3,4,5].map(i => <span key={i} className="fill-current drop-shadow-sm">★</span>)}
                    </div>
                    <p className="text-xs font-bold text-slate-700 tracking-wide uppercase">Verified Professionals</p>
                  </div>
              </div>
            </div>

            {/* Right: Enhanced Content & Benefits */}
            <div className="flex flex-col gap-10">
              <div className="space-y-6">
                <h2 className="text-4xl lg:text-5xl font-medium text-slate-900 leading-[1.15]">
                  Connecting you with{" "}
                  <span className="bg-gradient-to-r from-blue-600 to-emerald-500 bg-clip-text text-transparent">Quality Healthcare</span>
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                  Last minute wellness is Canada's premier platform for connecting clients with certified wellness professionals. We believe everyone deserves access to quality healthcare services that promote healing, wellness, and vitality.
                </p>
              </div>

              {/* Improved Feature Grid */}
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { 
                    icon: CheckCircle, 
                    title: "Top-Rated Experts", 
                    desc: "Verified and certified professionals" 
                  },
                  { 
                    icon: ClockIcon, 
                    title: "Instant Availability", 
                    desc: "Instant or quick appointment confirmations" 
                  },
                  { 
                    icon: MapPin, 
                    title: "National Coverage", 
                    desc: "Nationwide coverage across Canada" 
                  },
                  { 
                    icon: Locate, 
                    title: "Smart Matching", 
                    desc: "Secure and easy online booking" 
                  }
                ].map((feature, idx) => (
                   <div key={idx} className="flex gap-4 items-start p-4 rounded-2xl hover:bg-gray-50 transition-colors duration-300 border border-gray-200 hover:border-gray-100">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 mt-1">
                        <feature.icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-medium text-slate-900 text-lg">{feature.title}</h3>
                        <p className="text-slate-500 text-sm leading-snug ">{feature.desc}</p>
                      </div>
                   </div>
                ))}
              </div>

              <div>
                 <button 
                  onClick={() => navigate('/serviceList')}
                  className="px-8 py-3 bg-slate-900 text-white rounded-xl font-semibold shadow-lg shadow-slate-900/20 hover:shadow-xl hover:bg-slate-700 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto text-lg flex items-center justify-center gap-2 group"
                 >
                    Find Your Practitioner
                    {/* <ChevronDown className="w-5 h-5 -rotate-90 group-hover:translate-x-1 transition-transform" /> */}
                 </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* <FooterTop bg="theme-bg" /> */}
    </main>
  );
}
